import configService from "./configService";
import httpService from "./httpService";
import { getValidatedPostHogDeviceId } from "../utils/posthogDeviceId";
import { getCapturedAppBootProfile } from "../utils/adminCustomerViewBoot";
import { getReferrerOrigin } from "../utils/referrerOrigin";

const PUBLIC_PAGE_HOSTS = new Set([
  "www.runplutus.com", "app.runplutus.com", "runplutus.com",
  "dev.runplutus.com", "app-dev.runplutus.com",
  "staging.runplutus.com", "app-staging.runplutus.com",
]);
const CAMPAIGN_HEADERS = [
  ["utm_source", "X-UTM-Source"],
  ["utm_medium", "X-UTM-Medium"],
  ["utm_campaign", "X-UTM-Campaign"],
  ["utm_content", "X-UTM-Content"],
  ["utm_term", "X-UTM-Term"],
] as const;

interface PublicVisitPage {
  pageType: "home" | "share" | "login" | "signup";
  pageUrl: string;
  headers: Record<string, string>;
}

function getPublicVisitPage(pageUrl: string): PublicVisitPage | null {
  const visitUrl = new URL(pageUrl);
  if (visitUrl.protocol !== "https:" || !PUBLIC_PAGE_HOSTS.has(visitUrl.hostname)
    || visitUrl.port || visitUrl.username || visitUrl.password) return null;

  const pageType = visitUrl.pathname === "/" ? "home"
    : ["/login", "/mm-opm/login"].includes(visitUrl.pathname) ? "login"
      : visitUrl.pathname === "/signup" ? "signup" : "share";
  if (pageType === "share" && (
    !/^\/(?:picks|product-share)\/[A-Za-z0-9_-]{1,128}\/?$/.test(visitUrl.pathname)
    || /^\/product-share\/dev-preview\/?$/.test(visitUrl.pathname)
  )) return null;

  const campaignParameters = new URLSearchParams();
  const headers: Record<string, string> = {
    "X-Plutus-Visit-Source": pageType === "share"
      ? visitUrl.pathname.replace(/^\/picks\//, "/product-share/").replace(/\/$/, "")
      : visitUrl.pathname,
  };
  for (const [parameterName, headerName] of CAMPAIGN_HEADERS) {
    const parameterValue = visitUrl.searchParams.get(parameterName);
    if (parameterValue !== null) {
      // Avoid cutting a surrogate pair at the API's 200-code-unit boundary.
      const campaignValue = parameterValue.slice(0, 200).replace(/[\uD800-\uDBFF]$/, "");
      campaignParameters.set(parameterName, campaignValue);
      if (campaignValue) headers[headerName] = encodeURIComponent(campaignValue);
    }
  }
  // Drop auth query parameters, arbitrary query parameters and fragments from the visit URL.
  visitUrl.search = campaignParameters.toString();
  visitUrl.hash = "";
  if (visitUrl.href.length > 2048) return null;
  return { pageType, pageUrl: visitUrl.href, headers };
}

class PublicVisitService {
  async recordVisit(pageUrl: string, referrer: string): Promise<void> {
    try {
      const bootProfile = getCapturedAppBootProfile();
      const environment = configService.getEnvironment();
      if (!["dev", "staging", "prod"].includes(environment ?? "") || bootProfile.status !== "valid"
        || bootProfile.profile !== "ordinary") return;
      const visitPage = getPublicVisitPage(pageUrl);
      if (!visitPage) return;
      const anonymousId = getValidatedPostHogDeviceId();
      if (!anonymousId) return;

      await httpService.post("v3/public/visits", {
        anonymousId,
        eventId: crypto.randomUUID(),
        occurredAt: new Date().toISOString(),
        pageType: visitPage.pageType,
        pageUrl: visitPage.pageUrl,
        referrer: getReferrerOrigin(referrer),
      }, {
        headers: visitPage.headers,
        requiresAuth: false,
        // Optional identity for backend analytics; public visits never require or refresh a token.
        includeAuthIfAvailable: true,
        credentials: "omit",
        retryOnUnauthorized: false,
        keepalive: true,
        timeout: 5_000,
      });
    } catch {
      // Best effort: no retries, UI errors, credentials or request payloads in logs.
    }
  }
}

export const publicVisitService = new PublicVisitService();
